import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Play, Pause, Sparkles } from "lucide-react";
import { Slider } from "./ui/slider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PORTRAIT =
    "/assets/coach-melissa-headshot.jpg";

const fmt = (s) => {
    if (!s || !isFinite(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
};

export const Welcome = () => {
    const [settings, setSettings] = useState(null);
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);

    useEffect(() => {
        let mounted = true;
        axios
            .get(`${API}/settings`)
            .then((r) => mounted && setSettings(r.data))
            .catch(() => {});
        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        if (!settings?.welcome_audio_url) return;
        if (!audioRef.current) {
            audioRef.current = new Audio();
            audioRef.current.preload = "metadata";
        }
        audioRef.current.src = settings.welcome_audio_url;
        const audio = audioRef.current;
        const onTime = () => setProgress(audio.currentTime || 0);
        const onMeta = () => setDuration(audio.duration || 0);
        const onEnd = () => setIsPlaying(false);
        audio.addEventListener("timeupdate", onTime);
        audio.addEventListener("loadedmetadata", onMeta);
        audio.addEventListener("ended", onEnd);
        return () => {
            audio.pause();
            audio.removeEventListener("timeupdate", onTime);
            audio.removeEventListener("loadedmetadata", onMeta);
            audio.removeEventListener("ended", onEnd);
        };
    }, [settings?.welcome_audio_url]);

    if (!settings || !settings.welcome_audio_url) return null;

    const toggle = () => {
        const audio = audioRef.current;
        if (!audio) return;
        if (audio.paused) {
            audio.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
        } else {
            audio.pause();
            setIsPlaying(false);
        }
    };

    const seek = (v) => {
        const audio = audioRef.current;
        if (!audio || !duration) return;
        audio.currentTime = Math.min(Math.max(v[0], 0), duration);
        setProgress(audio.currentTime);
    };

    return (
        <section
            id="welcome"
            data-testid="welcome-section"
            className="relative pt-4 pb-20 md:pb-28"
        >
            <div className="mx-auto max-w-4xl px-6 md:px-10">
                <div className="relative overflow-hidden rounded-[36px] bg-white border border-border p-6 md:p-8 flex items-center gap-6 shadow-[0_18px_50px_rgba(43,29,53,0.06)]">
                    <div className="absolute -top-16 -right-12 w-60 h-60 rounded-full bg-gold/10 blur-3xl" aria-hidden />

                    {/* New badge */}
                    <span
                        data-testid="welcome-new-badge"
                        className="absolute top-4 right-4 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gold/15 text-gold-deep border border-gold/30 text-[10px] tracking-[0.14em] uppercase font-semibold shadow-[0_4px_12px_rgba(212,175,55,0.18)]"
                    >
                        <span className="relative flex h-1.5 w-1.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold opacity-70" />
                            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-gold-deep" />
                        </span>
                        New
                    </span>

                    {/* Portrait */}
                    <div className="relative shrink-0">
                        <img
                            src={PORTRAIT}
                            alt="Coach Melissa"
                            className="w-20 h-20 md:w-24 md:h-24 rounded-2xl object-cover border border-lilac-200"
                        />
                        {/* Soft pulse ring behind the play button (only when idle) */}
                        {!isPlaying && (
                            <span
                                aria-hidden
                                className="pointer-events-none absolute -bottom-2 -right-2 w-10 h-10 rounded-full bg-gold/50 animate-ping"
                            />
                        )}
                        <button
                            onClick={toggle}
                            data-testid="welcome-play"
                            aria-label={isPlaying ? "Pause welcome" : "Play welcome"}
                            className={`absolute -bottom-2 -right-2 grid place-items-center w-10 h-10 rounded-full bg-gold hover:bg-gold-deep text-white shadow-[0_8px_22px_rgba(212,175,55,0.45)] transition-transform hover:scale-105 ${
                                !isPlaying ? "welcome-play-pulse" : ""
                            }`}
                        >
                            {isPlaying ? (
                                <Pause className="w-4 h-4" fill="currentColor" />
                            ) : (
                                <Play className="w-4 h-4 ml-0.5" fill="currentColor" />
                            )}
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                        <div className="eyebrow text-gold-deep flex items-center gap-2 mb-2">
                            <Sparkles className="w-3.5 h-3.5" /> A note from Coach Melissa
                        </div>
                        <h3 className="font-display text-xl md:text-2xl text-lilac-900 leading-snug line-clamp-2">
                            {settings.welcome_title}
                        </h3>
                        <p className="text-sm text-lilac-700 mt-1 hidden md:block">
                            {settings.welcome_subtitle}
                        </p>

                        <div className="mt-3 flex items-center gap-3">
                            <span className="text-[11px] tabular-nums text-lilac-700 w-9">
                                {fmt(progress)}
                            </span>
                            <Slider
                                data-testid="welcome-scrub"
                                value={[progress]}
                                max={Math.max(duration, 0.0001)}
                                step={0.5}
                                onValueChange={seek}
                                className="flex-1"
                            />
                            <span className="text-[11px] tabular-nums text-lilac-700 w-9 text-right">
                                {fmt(duration) || settings.welcome_duration}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
